"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/** Premium expo-out curve — fast start, long settle (the "godly" easing). */
const EASE = [0.22, 1, 0.36, 1] as const;

interface MaskRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  /** Animation duration in seconds. */
  duration?: number;
  /** Play immediately on mount (for above-the-fold content) instead of on scroll-in. */
  immediate?: boolean;
}

/**
 * Clip-path style text reveal: the content rises into view from behind a mask
 * (an overflow-hidden line), the technique behind most award-winning headline
 * reveals. Plays once when scrolled into view (or immediately above the fold).
 * Renders plainly under reduced motion. Color/markup of children is preserved.
 */
export function MaskReveal({
  children,
  className,
  delay = 0,
  duration = 0.9,
  immediate = false,
}: MaskRevealProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-12%" });
  const show = immediate || inView;

  if (reduce) return <span className={className}>{children}</span>;

  return (
    <span
      ref={ref}
      className={`block overflow-hidden pb-[0.12em] ${className ?? ""}`}
    >
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: show ? 0 : "112%" }}
        transition={{ duration, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}
